import type { SpyReportLifeforms } from "@/api/types"
import { AllianceClass, AllianceClassFromID, CharacterClass, CharacterClassFromID } from "./classes"
import { defaultShips, type Coordinates, type Defenses, type Missiles, type Resources, type Ships, type Techs, defaultTechs, defaultResources, defaultShip, defaultDefenses, defaultMissiles, defaultDefense, defaultMissile } from "./types"

type Fleet = {
    ID: string
    API: string | null
    ships: Ships
    techs: Techs
    coordinates: Coordinates
    allianceClass: AllianceClass
    characterClass: CharacterClass

    speed: number
    fuelConsumption: number

    isDefender: boolean
    engineer: boolean
    resources: Resources
    defenses: Defenses
    missiles: Missiles
}

function NewFleet(isDefender: boolean): Fleet {
    let result: Fleet = {
        ID: crypto.randomUUID(),
        API: null,
        ships: defaultShips(),
        techs: defaultTechs(),
        coordinates: {
            galaxy: null,
            system: null,
            position: null
        },
        allianceClass: AllianceClass.None,
        characterClass: CharacterClass.None,

        speed: 100,
        fuelConsumption: 0,

        // defender fields
        isDefender: isDefender,
        engineer: false,
        resources: defaultResources(),
        defenses: {},
        missiles: {}
    }

    if (isDefender) {
        result.ships['212'] = defaultShip()
        result.ships['217'] = defaultShip()
        result.defenses = defaultDefenses()
        result.missiles = defaultMissiles()
    }

    return result
}

function NewFleetFromAPI(report: SpyReportLifeforms, isDefender: boolean): Fleet {
    let result: Fleet = {
        ID: crypto.randomUUID(),
        API: null,
        ships: defaultShips(),
        techs: defaultTechs(),
        coordinates: {
            galaxy: report.defender.planet.galaxy,
            system: report.defender.planet.system,
            position: report.defender.planet.position
        },
        allianceClass: AllianceClassFromID(report.defender.allianceClassId),
        characterClass: CharacterClassFromID(report.defender.characterClassId),

        speed: 100,
        fuelConsumption: 0,

        // defender fields
        isDefender: isDefender,
        engineer: false,
        resources: {
            metal: report.resources.metal,
            crystal: report.resources.crystal,
            deuterium: report.resources.deuterium,
            food: report.resources.food,
        },
        defenses: {},
        missiles: {}
    }

    if (isDefender) {
        result.ships['212'] = defaultShip()
        result.ships['217'] = defaultShip()
        result.defenses = defaultDefenses()
        result.missiles = defaultMissiles()
    }

    for (const entity in result.ships) {
        result.ships[entity] = report.defender.ships?.[entity] || defaultShip()
    }

    if (!isDefender) {
        return result
    }

    for (const entity in result.defenses) {
        result.defenses[entity] = report.defender.defenses?.[entity] || defaultDefense()
    }

    for (const entity in result.missiles) {
        result.missiles[entity] = report.defender.missiles?.[entity] || defaultMissile()
    }

    return result
}

function NewFleetFromAPIString(api: string, isDefender: boolean): Fleet {
    const data = Object.fromEntries(api.split('|').map(kv => kv.split(';')))
    let result: Fleet = {
        ID: crypto.randomUUID(),
        API: api,
        ships: defaultShips(),
        techs: {
            '109': data["109"] || 0,
            '110': data["110"] || 0,
            '111': data["111"] || 0,
            '114': data["114"] || 0,
            '115': data["115"] || 0,
            '117': data["117"] || 0,
            '118': data["118"] || 0
        },
        coordinates: {
            galaxy: parseInt(data['coords'].split(':')[0]),
            system: parseInt(data['coords'].split(':')[1]),
            position: parseInt(data['coords'].split(':')[2]),
        },
        allianceClass: AllianceClass.None,
        characterClass: CharacterClassFromID(parseInt(data["characterClassId"] || 0)),

        speed: 100,
        fuelConsumption: 0,

        // defender fields
        isDefender: isDefender,
        engineer: false,
        resources: defaultResources(),
        defenses: {},
        missiles: {}
    }

    
    if (isDefender) {
        result.ships["212"] = defaultShip();
        result.ships["217"] = defaultShip();

        result.defenses = defaultDefenses()
        for (const entity in result.defenses) {
            result.defenses[entity].amount = parseInt(data[entity], 10) || null;
        }
    }

    for (const entity in result.ships) {
        result.ships[entity].amount = parseInt(data[entity], 10) || null;
    }

    return result
}

type fleetJson = {
    coords: string
    characterClassId: number
    allianceClassId: number
    research: {[key: string]: number}
    defenses: {[key: string]: {
        amount: number
        weapon: number
        shield: number
        armor: number
    }}
    ships: {[key: string]: {
        amount: number
        weapon: number
        shield: number
        armor: number
        cargo: number
        speed: number
        fuel: number
    }}
    missiles: {[key: string]: { amount: number }}
    bonuses: {
        recycleAttackerFleet: number,
        moonChanceIncrease: number,
        lifeformProtection: number,
        spaceDockExtender: number,
        denCapacity: {
            metal: number
            crystal: number
            deuterium: number
        }
    }
}

function NewFleetFromAPIJson(api: string, isDefender: boolean): Fleet {
    const data: fleetJson = JSON.parse(api)

    console.log('api json data', data)
    
    let result: Fleet = {
        ID: crypto.randomUUID(),
        API: api,
        ships: defaultShips(),
        techs: {
            '109': data.research["109"],
            '110': data.research["110"],
            '111': data.research["111"],
            '114': data.research["114"],
            '115': data.research["115"],
            '117': data.research["117"],
            '118': data.research["118"]
        },
        coordinates: {
            galaxy: parseInt(data['coords'].split(':')[0]),
            system: parseInt(data['coords'].split(':')[1]),
            position: parseInt(data['coords'].split(':')[2]),
        },
        allianceClass: AllianceClassFromID(data.allianceClassId),
        characterClass: CharacterClassFromID(data.characterClassId),

        speed: 100,
        fuelConsumption: 0,

        // defender fields
        isDefender: isDefender,
        engineer: false,
        resources: defaultResources(),
        defenses: {},
        missiles: {}
    }

    
    if (isDefender) {
        result.ships["212"] = defaultShip();
        result.ships["217"] = defaultShip();

        result.missiles = defaultMissiles()
        result.defenses = defaultDefenses()
        for (const entity in result.defenses) {
            result.defenses[entity] = data.defenses[entity];
        }
    }

    for (const entity in result.ships) {
        result.ships[entity] = data.ships[entity];

        if (data.ships[entity].amount == 0) {
            result.ships[entity].amount = null
        }
    }

    return result
}

export type {
    Fleet
}

export {
    NewFleet,
    NewFleetFromAPI,
    NewFleetFromAPIJson,
    NewFleetFromAPIString,
}